import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { useUnlockMonth } from "@/hooks/use-unlock-month";
import { formatPct, MONTH_NAMES_NB } from "@/lib/format";
import { billingStatus, TONE_BG, TONE_TEXT } from "@/lib/widget-status";
import { cn } from "@/lib/utils";

interface Month {
  year: number;
  month: number;
  rate: number | null;
}

interface MonthlyRateListProps {
  userId: string;
  year: number;
  months: Month[];
  target: number | null;
  lockedMonths: Set<number>;
  /** Admin kan gjenåpne en låst måned med begrunnelse (VIB-1649). */
  canUnlock: boolean;
  /** Åpner detaljvisningen for måneden. Utelatt når brukeren mangler tilgang. */
  onOpenMonth?: (month: number) => void;
}

/** Faktureringsgrad per måned som stolper mot målstreken. */
export function MonthlyRateList({ userId, year, months, target, lockedMonths, canUnlock, onOpenMonth }: MonthlyRateListProps) {
  const { mutate: unlockMonth, isPending: isUnlocking } = useUnlockMonth();
  const [unlockTarget, setUnlockTarget] = useState<number | null>(null);
  const [unlockReason, setUnlockReason] = useState("");

  const closeUnlock = () => {
    setUnlockTarget(null);
    setUnlockReason("");
  };

  return (
    <ul className="text-sm">
      {months.map((m) => {
        const isLocked = lockedMonths.has(m.month);
        const tone = m.rate != null && target != null ? billingStatus(m.rate, target).tone : null;
        const rateText = m.rate != null ? formatPct(m.rate) : "—";
        return (
          <li
            key={`${m.year}-${m.month}`}
            className="grid grid-cols-[2.75rem_minmax(0,1fr)_4.5rem_1rem_5.5rem] items-center gap-3 border-b border-border/60 py-2 last:border-0"
          >
            <span className="text-muted-foreground">{MONTH_NAMES_NB[m.month - 1].slice(0, 3)}</span>
            <div className="relative h-2 rounded-full bg-muted" aria-hidden="true">
              <span
                className={cn("absolute inset-y-0 left-0 rounded-full", tone ? TONE_BG[tone] : "bg-muted-foreground/40")}
                style={{ width: `${Math.min(m.rate ?? 0, 100)}%` }}
              />
              {target != null && (
                <span className="absolute -inset-y-1 w-0.5 bg-foreground" style={{ left: `${Math.min(target, 100)}%` }} />
              )}
            </div>
            {onOpenMonth ? (
              <button
                type="button"
                onClick={() => onOpenMonth(m.month)}
                className={cn("text-right font-medium tabular-nums underline decoration-dotted underline-offset-2", tone && TONE_TEXT[tone])}
              >
                {rateText}
              </button>
            ) : (
              <span className={cn("text-right font-medium tabular-nums", tone && TONE_TEXT[tone])}>{rateText}</span>
            )}
            <span>
              {isLocked && (
                <SrIcon name="lock" className="h-3.5 w-3.5 text-muted-foreground" title="Måneden er lukket og låst" />
              )}
            </span>
            <span className="text-right">
              {canUnlock && isLocked && unlockTarget !== m.month && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-muted-foreground"
                  onClick={() => {
                    setUnlockTarget(m.month);
                    setUnlockReason("");
                  }}
                >
                  Gjenåpne
                </Button>
              )}
            </span>
            {canUnlock && isLocked && unlockTarget === m.month && (
              <span className="col-span-full flex flex-wrap items-center justify-end gap-1">
                <Input
                  value={unlockReason}
                  onChange={(event) => setUnlockReason(event.target.value)}
                  aria-label="Begrunnelse for gjenåpning"
                  placeholder="Begrunnelse"
                  className="h-8 w-52 text-xs"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 px-2 text-xs"
                  disabled={isUnlocking || !unlockReason.trim()}
                  onClick={() =>
                    unlockMonth({ userId, year, month: m.month, reason: unlockReason }, { onSuccess: closeUnlock })
                  }
                >
                  {isUnlocking ? "Låser opp…" : "Bekreft"}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={isUnlocking} onClick={closeUnlock}>
                  Avbryt
                </Button>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
